import { i18n } from '../../../i18n/translate';
import { getData } from '../../../json-api/JsonApiUtils';
import { sortBy as lodashSortBy } from 'lodash';
import { getPageSize } from '../../../utils/pageSize';
import { ScrollElementToTarget } from '../../../utils/WindowHelper';
import React from 'react';
import PropTypes from 'prop-types';
import { FormControl } from '../Controls/FormControl';
import { Button } from '../../Button';
import { FAIcon } from '../../fa-icon';
import { Chip } from '../../Chip';
import { pickDataAttributes } from '../../../utils';

export const autoComplete = props => {
  return props.autocomplete && props.autocomplete === 'off'
    ? { autoComplete: 'nope', autoCorrect: 'off', spellCheck: 'off' }
    : {};
};

const isValidRequiredValue = props => {
  const { value } = props;

  if (value instanceof Array) {
    return value.length > 0;
  }

  return !!getDefaultOptionValue(value, props);
};

export const checkSelectError = (props = {}, select = {}) => {
  if (typeof props.setError === 'function') {
    if (props.required && !isValidRequiredValue(select.props)) {
      const fieldLabel = props.caption || props.name;
      const message = fieldLabel
        ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
        : i18n.t('This field is required');
      props.setError(true, message);
    } else {
      props.setError(false);
    }
  }
};

export const checkError = (props, select) => {
  if (typeof props.setError === 'function') {
    const input = select?.wrapper?.querySelector('input');
    if (props.required && (!input || !input.value)) {
      const fieldLabel = props.caption || props.name;
      const message = fieldLabel
        ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
        : i18n.t('This field is required');
      props.setError(true, message);
    } else {
      props.setError(false);
    }
  }
};

export const getDefaultOptionLabel = (option = {}, props = {}) => {
  const { displayProperty, labelKey } = props;

  return (
    option?.[labelKey] ??
    option?.attributes?.[labelKey] ??
    option?.[displayProperty] ??
    option?.attributes?.[displayProperty] ??
    option?.display ??
    option?.label
  );
};

export const getDefaultOptionValue = (option = {}, props = {}) => {
  const { valueKey, valueProperty } = props;

  return (
    option?.[valueKey] ??
    option?.attributes?.[valueKey] ??
    option?.data?.[valueKey] ??
    option?.[valueProperty] ??
    option?.attributes?.[valueProperty] ??
    option?.data?.[valueProperty] ??
    option?.value ??
    option?.id
  );
};

const getValueObject = (value, options = [], props = {}) => {
  const { creatable, displayProperty, labelKey, valueKey, valueProperty } = props;

  if (typeof value !== 'object') {
    const filteredOptions = options?.filter?.(option => {
      const optionDisplayProperty = option?.[displayProperty] ?? option?.attributes?.[displayProperty];
      const optionLabelKey = option?.[labelKey] ?? option?.attributes?.[labelKey];
      const optionValueKey = option?.[valueKey] ?? option?.attributes?.[valueKey];
      const optionValueProperty = option?.[valueProperty] ?? option?.attributes?.[valueProperty];

      return Object.values({
        ...(option?.display && { display: option.display }),
        ...(option?.id && { id: option.id }),
        ...(option?.label && { label: option.label }),
        ...(option?.value && { value: option.value }),
        ...(optionDisplayProperty && { [displayProperty]: optionDisplayProperty }),
        ...(optionLabelKey && { [labelKey]: optionLabelKey }),
        ...(optionValueKey && { [valueKey]: optionValueKey }),
        ...(optionValueProperty && { [valueProperty]: optionValueProperty }),
      })?.includes?.(String(value));
    });

    if (filteredOptions?.length > 0) {
      return filteredOptions?.[0];
    }

    if (creatable) {
      return {
        ...(displayProperty && { [displayProperty]: value }),
        ...(labelKey && { [labelKey]: value }),
        ...(valueKey && { [valueKey]: value }),
        ...(valueProperty && { [valueProperty]: value }),
        disabled: false,
        display: value,
        id: value,
        label: value,
        value,
      };
    }
  }

  return value;
};

export const getFormattedValue = (value, options = [], props = {}) => {
  const { multi, isMulti } = props;

  if (!multi && !isMulti) {
    return getValueObject(value, options, props);
  }

  return value?.map?.(val => getValueObject(val, options, props))?.filter?.(val => typeof val === 'object') ?? [];
};

export const getSelectData = async (input, props, clientSideFilter) => {
  const {
    authToken,
    buildDisplay,
    dataUrl,
    disabledOptionsIds,
    displayProperty,
    labelKey,
    noSort,
    onDataReady,
    optionsToAppend,
    optionsToPrepend,
    pageNumber,
    pageSize,
    queryFields,
    sortBy,
    valueKey,
    valueProperty,
  } = props;

  let queryParams;
  if (queryFields && input) {
    queryParams = {
      name: {
        term: input,
        fields: queryFields,
      },
    };
  }

  const { data, meta } = await getData(dataUrl, {
    authToken,
    filters: {
      ...props.filters,
      ...(props.filterField && input && !queryFields ? { [props.filterField]: input } : undefined),
    },
    sorts: sortBy && [{ value: sortBy, direction: 'asc' }],
    page: { number: pageNumber || 1, size: pageSize || getPageSize() },
    query: queryParams,
  });

  let options = data.map(i => {
    const buildDisplayVal = buildDisplay && buildDisplay(i);
    const disabled = !!disabledOptionsIds.length && disabledOptionsIds?.includes?.(i.id);
    const valueKeyObj =
      valueKey ||
      (valueProperty && {
        [valueKey ?? valueProperty]: i?.[valueKey ?? valueProperty] ?? i?.attributes?.[valueKey ?? valueProperty],
      });
    const labelKeyObj = labelKey && { [labelKey]: i?.[labelKey] ?? i?.attributes?.[labelKey] };
    // label is derived from value
    const valueLabelKeyObj = !labelKey &&
      !displayProperty &&
      (valueKey || valueProperty) && {
        label: i?.[valueKey ?? valueProperty] ?? i?.attributes?.[valueKey ?? valueProperty],
      };
    const displayPropertyObj = displayProperty && {
      [labelKey || 'label']: i?.[displayProperty] ?? i?.attributes?.[displayProperty],
    };
    // value is derived from displayProperty
    const valueDisplayPropertyObj = displayProperty &&
      !valueProperty &&
      !valueKey && { value: i?.[displayProperty] ?? i?.attributes?.[displayProperty] };

    return {
      ...valueKeyObj,
      ...labelKeyObj,
      ...valueLabelKeyObj,
      data: i,
      disabled,
      display: buildDisplayVal || `${i[displayProperty]}`,
      ...displayPropertyObj,
      ...valueDisplayPropertyObj,
    };
  });
  if (!noSort) {
    options = lodashSortBy(options, option => option.display?.toLowerCase?.());
  }

  /*
    postGetOptions. Similar to buildDisplay except
    used for taking ALL options, making changes, then sending
    them all back in as one object.
  */
  if (props.postGetOptions) {
    options = await props.postGetOptions(options);
  }

  if (optionsToPrepend) options = optionsToPrepend.concat(options);
  if (optionsToAppend) options = optionsToAppend.concat(options);

  if (onDataReady) onDataReady(options, meta);

  // filter for async search
  if (clientSideFilter && input) {
    options =
      options?.filter?.(option => String(option?.display).toLowerCase()?.includes?.(String(input).toLowerCase())) ?? [];
  }

  return options ?? [];
};

export const selectOnChange = (value, props, select, skipValueChange) => {
  if (typeof props.onChange === 'function') {
    props.onChange(value);
  }

  if (typeof props.onValueChange === 'function' && !skipValueChange) {
    if (!value) return;

    let changedValue;

    if (value instanceof Array) {
      changedValue = value.map(val => getDefaultOptionValue(val, props));
    } else {
      changedValue = getDefaultOptionValue(value, props);
    }

    props.onValueChange({ [props.name]: changedValue });
  }

  if (typeof select?.props?.loadOptions === 'function' && !value && select && props.dataUrl) {
    select.props.loadOptions(null);
  }

  return value;
};

export const onFocus = (e, props) => {
  const el = !props.scrollContainerId ? window : document.getElementById(props.scrollContainerId);
  const target = e.target;
  ScrollElementToTarget(el, target, 200);
  if (typeof props.onFocus === 'function') {
    props.onFocus(e);
  }
};

export const translationProps = {
  searchPromptText: i18n.t('Type to search'),
  noResultsText: i18n.t('No results found'),
  loadingPlaceholder: i18n.t('Loading...'),
  clearAllText: i18n.t('Clear all'),
  clearValueText: i18n.t('Clear value'),
};

// react-select only speaks an option's selected/disabled state through this live region on Apple
// devices (VoiceOver); on other platforms (e.g. NVDA) it relies on `aria-selected`, which NVDA
// does not reliably announce for `aria-activedescendant`-based listboxes. Announcing it here for
// every platform fixes that gap without touching react-select's Apple-only behavior.
const getFocusedOptionIndex = (options, focused) =>
  options && options.length ? `${options.indexOf(focused) + 1} of ${options.length}` : '';

export const ariaLiveMessages = {
  onFocus: props => {
    const { context, focused, options, label = '', selectValue, isDisabled, isSelected } = props;

    if (context === 'value' && selectValue) {
      return `value ${label} focused, ${getFocusedOptionIndex(selectValue, focused)}.`;
    }

    if (context === 'menu') {
      const status = `${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`;
      return `${label}${status}, ${getFocusedOptionIndex(options, focused)}.`;
    }

    return '';
  },
};

export const defaultMultiselectProps = Object.assign({}, FormControl.defaultProps, {
  creatable: false,
  disabledOptionsIds: [],
  filters: {},
  placeholderText: '',
  requireInput: false,
  valueChangeDelay: 500,
  valueProperty: 'id',
  isLegacyOverride: true,
  clientSideFilter: false,
});

export const rendererProps = (multi, props = null) => {
  const dataAttributeProps = props && pickDataAttributes(props);

  const SingleValueComponent = val => {
    const { children, id } = val;
    return (
      <div className="Select-value" {...dataAttributeProps}>
        <span aria-selected="true" className="Select-value-label" id={id} role="option">
          {children}
        </span>
      </div>
    );
  };

  const MultiValueComponent = val => {
    const { children, disabled, onClick, onRemove, value } = val;
    const performOnClick = () => {
      if (onClick) {
        onClick(value);
      }
    };
    const performOnRemove = () => {
      onRemove(value);
    };

    return (
      <Chip
        value={React.Children.toArray(children)[0]}
        disabled={disabled}
        onClick={performOnClick}
        onRemove={performOnRemove}
      />
    );
  };

  return {
    arrowRenderer: () => <FAIcon icon="angle-down" />,
    clearRenderer: () => <Button style="link" icon="xmark" />,
    valueComponent: multi ? MultiValueComponent : SingleValueComponent,
  };
};

export const multiSelectPropTypes = {
  authToken: PropTypes.string,
  buildDisplay: PropTypes.func,
  cacheEnabled: PropTypes.bool,
  children: PropTypes.any,
  clientSideFilter: PropTypes.bool,
  creatable: PropTypes.bool,
  dataUrl: PropTypes.string.isRequired,
  dataValue: PropTypes.any,
  disabled: PropTypes.bool,
  disabledOptionsIds: PropTypes.array,
  displayProperty: PropTypes.string,
  draggable: PropTypes.bool,
  filterField: PropTypes.string.isRequired,
  filters: PropTypes.object,
  initialData: PropTypes.any,
  noSort: PropTypes.bool,
  onChange: PropTypes.func,
  onDataReady: PropTypes.func,
  optionsToAppend: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      display: PropTypes.string,
    })
  ),
  optionsToPrepend: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      display: PropTypes.string,
    })
  ),
  pageSize: PropTypes.number,
  postGetOptions: PropTypes.func,
  queryFields: PropTypes.object,
  requireInput: PropTypes.bool,
  scrollContainerId: PropTypes.string,
  sortBy: PropTypes.string,
  valueChangeDelay: PropTypes.number,
  valueProperty: PropTypes.string,
  isLegacyOverride: PropTypes.bool,
};
